import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '@/components/MarketingLayout';
import { usePageSEO } from '@/utils/seo';
import {
  ORGANIZATION_SCHEMA, FOUNDER_SCHEMA, WEBSITE_SCHEMA, PRODUCT_SCHEMA,
  makeBreadcrumb, makeFAQ,
} from '@/utils/schema';
import { ArrowRight, CheckCircle, CaretDown } from '@phosphor-icons/react';

const FAQ = [
  {
    q: 'Is PayGBot a course?',
    a: 'No. PayGBot is not a course. It is an AI business operating system that helps you build and run an online business through guided systems, automation, and done-for-you assets — not lessons, modules, or homework. You can start using it the same day you sign up.',
  },
  {
    q: 'Can beginners use PayGBot?',
    a: 'Yes. PayGBot was designed for beginners and busy people who have never built a business before. It walks you through every decision step by step, generates the assets you need, and gives you a daily action plan so you always know what to do next.',
  },
  {
    q: 'What does PayGBot create?',
    a: 'PayGBot creates business plans, offers, lead magnets, sales pages, email sequences, social media content, video scripts, hooks, captions, weekly action plans, and daily business workflows — all generated for your specific business and saved inside your account.',
  },
  {
    q: 'Does PayGBot generate content?',
    a: 'Yes. The Content Bot inside PayGBot generates hooks, captions, scripts, video prompts, content ideas, and a full posting plan tailored to your niche and audience. The Growth Bot then schedules it into your week so you always have something to post.',
  },
  {
    q: 'Does PayGBot build sales pages and lead magnets?',
    a: 'Yes. PayGBot includes a Sales Page Builder, a Lead Magnet Builder, an Email Sequence Builder, an Ebook Builder, plus DFY Video, Voiceover, Brand Kit, Logo, and Premium Image builders. On the Business Strategist plan every asset is generated as a finished, downloadable product using your Business Credits — no separate tiers, no template downgrade.',
  },
  {
    q: 'Is PayGBot designed for busy people?',
    a: 'Yes — that is the entire reason it exists. PayGBot was built for parents, side hustlers, caregivers, and people rebuilding after difficult seasons of life. The platform is designed to be used in minutes a day, not hours.',
  },
  {
    q: 'Can PayGBot help me if I do not know what business to start?',
    a: 'Yes. The Business Builder Bot will ask you a few simple questions about your time, your skills, and your goals, then suggest a clear, beginner-friendly direction with a real income roadmap. You do not need an idea before you start — PayGBot helps you find one.',
  },
  {
    q: 'What makes PayGBot different from ChatGPT?',
    a: 'ChatGPT is a general-purpose assistant. PayGBot is a complete business operating system. It remembers your business, generates real assets you can sell, schedules your week, tracks your progress, builds your sales pages, writes your emails, and gives you daily guidance — none of which a generic AI chat does on its own.',
  },
  {
    q: 'What are Run My Week and Run My Business?',
    a: 'Run My Week generates a 7-day action plan tailored to your business so you always know exactly what to do each day. Run My Business is a deeper guided workflow that walks you through running a real business week by week — from offer to launch to scale — without having to figure anything out yourself.',
  },
  {
    q: 'How long does setup take?',
    a: 'Most people complete their initial business setup inside PayGBot in under 30 minutes. By the end of your first week, you will have a working business plan, your first offer, a 7-day action plan, content scheduled, and a system to keep growing every week.',
  },
];

export default function WhatIsPayGBotPage() {
  const [open, setOpen] = useState(0);

  usePageSEO({
    title: 'What Is PayGBot? | AI Business Operating System',
    description:
      'PayGBot is an AI business operating system designed to help busy people build and run a business in minutes a day using automation, content systems, offers, and done-for-you business tools.',
    path: '/what-is-paygbot',
    keywords:
      'PayGBot, AI business operating system, build a business in minutes a day, business automation, beginner business tools, simplified business systems, Run My Week, Run My Business',
    jsonLd: [
      ORGANIZATION_SCHEMA,
      FOUNDER_SCHEMA,
      WEBSITE_SCHEMA,
      PRODUCT_SCHEMA,
      makeBreadcrumb([
        { name: 'Home', path: '/' },
        { name: 'What Is PayGBot?', path: '/what-is-paygbot' },
      ]),
      makeFAQ(FAQ),
    ],
  });

  return (
    <MarketingLayout>
      <article data-testid="what-is-paygbot-article">
        {/* Hero */}
        <section className="px-4 sm:px-6 lg:px-8 pt-16 pb-10 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-3xl mx-auto text-center space-y-5">
            <p className="text-xs uppercase tracking-widest text-blue-600 font-semibold">AI Business Operating System</p>
            <h1 data-testid="what-is-paygbot-h1" className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              What Is PayGBot?
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              PayGBot is an AI business operating system that helps busy people build and run a real business in minutes a day — without courses, without overwhelm, and without piecing together a dozen disconnected tools.
            </p>
            <div className="pt-2 flex flex-wrap justify-center gap-3">
              <Link to="/signup" className="inline-flex items-center space-x-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors">
                <span>Start My 7-Day Free Trial</span>
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/founder-story" className="inline-flex items-center px-5 py-3 border border-slate-300 hover:border-slate-400 text-slate-700 font-medium rounded-lg">
                Read the Founder Story
              </Link>
            </div>
          </div>
        </section>

        {/* What it is */}
        <section className="px-4 sm:px-6 lg:px-8 py-14 bg-white">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">PayGBot is not a course. PayGBot is not just another AI tool.</h2>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                Most people who try to build an online business get stuck in the same loop: too many courses, too many tools, too much information, and not enough actual progress. They buy a $497 course, get overwhelmed by a 12-module training, never finish it, and feel like they failed.
              </p>
              <p>
                PayGBot replaces that loop with a working business system. Instead of teaching you what to do, it does the work with you — generating your business plan, your offer, your lead magnets, your sales pages, your content, your email sequences, and your weekly action plan, all inside one connected platform.
              </p>
              <p>
                It is an <strong>AI business operating system</strong>: the same way a phone has an operating system that runs every app on it, PayGBot is the system that runs every part of your online business. You bring your time and your decisions. PayGBot brings the workflows, the automation, the content, the assets, and the daily guidance.
              </p>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 rounded-xl p-5 border border-slate-200">
              {[
                'Simplified business building — no more endless learning',
                'Automation for content, offers, and outreach',
                'Done-for-you business assets (Pro plan)',
                'Daily guidance built for real life',
                'Beginner-friendly workflows you can actually finish',
                'One platform instead of fifteen disconnected tools',
              ].map((line) => (
                <div key={line} className="flex items-start gap-2">
                  <CheckCircle size={20} weight="fill" className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">{line}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="px-4 sm:px-6 lg:px-8 py-14 bg-slate-50 border-y border-slate-200">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">How PayGBot Works</h2>
            <p className="text-slate-700 leading-relaxed">
              PayGBot runs your business through four connected bots and two execution layers. You move through them in order, but everything stays linked together — your business memory carries from one bot to the next, so nothing has to be re-explained or re-typed.
            </p>

            <div className="space-y-4">
              {[
                { name: 'Business Builder Bot', desc: 'Creates your business plan, your direction, and your income roadmap. You answer a few questions; PayGBot builds the foundation. Whether you already have an idea or have no idea where to start, it will produce a clear beginner-friendly direction with first steps.' },
                { name: 'Money Bot', desc: 'Builds your first offer, your sales page, your email sequence, and the path your audience takes to actually pay you. Every asset ships as a finished, downloadable deliverable — powered by Business Credits.' },
                { name: 'Content Bot', desc: 'Generates the hooks, captions, scripts, video prompts, and content ideas you need to actually post. It pulls from your business profile so every piece sounds like your business — not a generic prompt output.' },
                { name: 'Growth Bot', desc: 'Plans your week, tracks your progress, scores your content, and tells you what to fix. Surfaces an 8-stage progression engine that moves your business forward instead of looping you back through the same content checklist.' },
                { name: 'Run My Week', desc: 'A complete 7-day action plan generated specifically for your business and your time budget. Each day shows you exactly what to do, where to post it, and what tier-appropriate asset to use (direction, template, or done-for-you copy).' },
                { name: 'Run My Business', desc: 'A deeper guided workflow that walks you through running a real business week by week — offers, launch, lead generation, email, sales, and scale — without you having to assemble the path yourself.' },
              ].map((b) => (
                <div key={b.name} className="bg-white rounded-xl p-5 border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-1">{b.name}</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>

            <p className="text-slate-700 leading-relaxed">
              Behind those bots, PayGBot runs an automation layer that handles content generation, sales page rendering, email sequences, ebook bundling (PDF + DOCX + HTML), lead magnet creation, calendar scheduling, and daily content delivery — all without you needing to wire anything together.
            </p>
          </div>
        </section>

        {/* Who it is for */}
        <section className="px-4 sm:px-6 lg:px-8 py-14 bg-white">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">Who PayGBot Is For</h2>
            <p className="text-slate-700 leading-relaxed">
              PayGBot was built for people whose life does not give them eight hours a day to figure out an online business. The kind of people most platforms forget about.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { who: 'Busy parents', detail: 'who need a system they can run during nap times, school hours, or after bedtime.' },
                { who: 'Beginners', detail: 'with no idea what business to start, but real willingness to follow a clear path.' },
                { who: 'Side hustlers', detail: 'who already have a full-time job and need a business that fits around it.' },
                { who: 'Creators', detail: 'who can make content but need a real product, real funnel, and real income plan to attach it to.' },
                { who: 'Small business owners', detail: 'who already have something running and need automation, content, and growth systems.' },
                { who: 'Caregivers', detail: 'building income flexibility around a family member who needs them.' },
                { who: 'People rebuilding', detail: 'after job loss, illness, divorce, or any season of life that closed an old chapter.' },
                { who: 'Anyone tired of courses', detail: 'who has bought enough trainings and is ready for a system that actually runs.' },
              ].map((p) => (
                <div key={p.who} className="border border-slate-200 rounded-xl p-4">
                  <p className="font-semibold text-slate-900 text-sm mb-1">{p.who}</p>
                  <p className="text-sm text-slate-600">{p.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why different */}
        <section className="px-4 sm:px-6 lg:px-8 py-14 bg-slate-50 border-y border-slate-200">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">Why PayGBot Is Different</h2>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                Most online business platforms fall into one of two buckets. Either they are <em>courses</em> that hand you information and expect you to assemble a business from it, or they are <em>generic AI tools</em> that hand you a blank chat and expect you to know what to ask.
              </p>
              <p>PayGBot is neither.</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>It is not a course.</strong> There are no lessons, modules, or homework to finish. You produce real business assets the same day you sign up.</li>
                <li><strong>It is not endless training.</strong> Every screen is built to move your business forward, not your knowledge level.</li>
                <li><strong>It is not generic AI prompts.</strong> The platform already knows your business — your niche, your offer, your audience, your goals — so nothing has to be re-explained on every request.</li>
                <li><strong>It is focused on implementation.</strong> Sales pages, lead magnets, ebooks, emails, content, action plans — produced for your specific business and saved in your account.</li>
                <li><strong>It is focused on simplicity.</strong> One platform, one login, one daily action plan. No more stitching tools together.</li>
                <li><strong>It is focused on speed.</strong> You can have a real offer ready in an afternoon, content scheduled in a week, and a running business system inside the first month.</li>
                <li><strong>It is built for real-world use.</strong> Designed by a founder who has built businesses through hard seasons — not by a venture-funded team optimizing for power users.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Founder */}
        <section className="px-4 sm:px-6 lg:px-8 py-14 bg-white">
          <div className="max-w-3xl mx-auto space-y-5">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">Why PayGBot Exists</h2>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                PayGBot was built by <strong>Vickie Mcglawn</strong>. She has spent decades building businesses across bookkeeping, accounting, contractor compliance scheduling, commercial roofing, home improvement, logging, and trucking — the kind of businesses with real operations, real margins, and real consequences if something is missed.
              </p>
              <p>
                When her husband became seriously sick with an autoimmune disease, life changed quickly. During the six months it took to get a diagnosis, she was also homeschooling twins. The traditional way she had been running businesses was no longer possible. She needed flexibility — and she needed it without losing the income her family depended on.
              </p>
              <p>
                During that season, she started learning online income systems and affiliate marketing. She promoted products and services using Google Ads and social media, and saw firsthand that online business systems — when designed correctly — could create the freedom and flexibility that traditional businesses rarely allow.
              </p>
              <p>
                But the deeper she went, the more she noticed how overwhelming and complicated most online business education had become. Endless courses. Confusing funnels. Constant content pressure. Too many disconnected tools that never quite worked together.
              </p>
              <p>
                PayGBot is her answer to that frustration. It is the platform she wished existed when she needed flexibility, simplicity, and a real way to build income without losing her life to it.
              </p>
              <p className="text-slate-900 font-medium">
                The goal of PayGBot is simple: help real people build and run real businesses in minutes a day — without overwhelm, without confusion, and without another course.
              </p>
            </div>
            <div className="pt-2">
              <Link to="/founder-story" className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium">
                <span>Read the full founder story</span>
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ accordion */}
        <section className="px-4 sm:px-6 lg:px-8 py-14 bg-slate-50 border-t border-slate-200">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3" data-testid="what-is-paygbot-faq">
              {FAQ.map((item, idx) => {
                const isOpen = open === idx;
                return (
                  <div key={item.q} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? -1 : idx)}
                      className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left"
                      data-testid={`faq-q-${idx}`}
                    >
                      <span className="font-semibold text-slate-900">{item.q}</span>
                      <CaretDown
                        size={18}
                        weight="bold"
                        className={`text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 text-slate-700 leading-relaxed text-sm" data-testid={`faq-a-${idx}`}>
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 sm:px-6 lg:px-8 py-16 bg-white">
          <div className="max-w-2xl mx-auto text-center space-y-5">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">Ready to build your business in minutes a day?</h2>
            <p className="text-slate-600">Start your 7-day free trial. After the trial, continue on Business Strategist for $49/month. No course. No homework. Just a system that runs.</p>
            <Link to="/signup" className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors">
              <span>Start My Free Trial</span>
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </section>
      </article>
    </MarketingLayout>
  );
}
